import { Stack, Typography } from "@mui/material";
import { Fragment, useEffect, useRef, useState } from "react";
import { BackButton } from "react-common-ui-library/FPACT/Buttons";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  BACK_BUTTON_TEXT,
  FORGOT_USERNAME_EMAIL_SENT,
  FORGOT_USERNAME_ROUTE,
  H1,
  H3,
  IS_RECAPTCHA_ENABLED,
  LOGIN_ROUTE,
  MODE_ALL,
} from "../../../Constants/CommonConstants";
import {
  FORGOT_USERNAME_BACK_BUTTON,
  FORGOT_USERNAME_CONTENT_1,
  FORGOT_USERNAME_FORM,
  FORGOT_USERNAME_HEADER,
  MATCH_NOT_FOUND,
  USERNAME_SENT_SUCCESSFULLY,
} from "../../../Constants/ForgotUsernameConstants";
import {
  DATE_OF_BIRTH_FIELD,
  EMAIL_FIELD,
} from "../../../Constants/LoginConstants";
import { FLOW_TEXT_ACCOUNT } from "../../../Constants/NoMatchFoundModalConstants";
import {
  SLICE_LOGIN,
  THUNK_POST_FORGOT_USERNAME,
} from "../../../Constants/SliceAndThunkConstants";
import {
  clearLoginApiResponse,
  postForgotUsername,
  saveForgotUsernameData,
} from "../../../Features/LoginSlice";
import { manageApiResponseSelector } from "../../../Selectors/ApiResponseSelector";
import { manageLoginSelector } from "../../../Selectors/LoginSelector";
import { StyledTypographyHeader } from "../../../Styles/Common/CommonStyles";
import { StyledForgotUsernameBox } from "../../../Styles/Login/ForgotUsername/ForgotUsernameStyles";
import GoogleReCaptcha from "../../Application/GoogleReCaptcha";
import NoMatchFoundModal from "../../Common/NoMatchFoundModal/NoMatchFoundModal";
import ForgotUsernameFormElements from "./ForgotUsernameFormElements";
import ConfigService from "../../../Services/ConfigService";

const isReCaptchaEnabled = ConfigService(IS_RECAPTCHA_ENABLED);

/**
 * @description Forgot username page that collects date of birth and email username retrieval.
 * @returns {ReactNode} Render the Email and Date of birth inputs with action buttons.
 */
const ForgotUserName = () => {
  const intl = useIntl();

  const { dateOfBirth, emailId } = useSelector((state) =>
    manageLoginSelector(state)
  );

  const methods = useForm({
    mode: MODE_ALL,
    defaultValues: {
      [DATE_OF_BIRTH_FIELD]: dateOfBirth,
      [EMAIL_FIELD]: emailId,
    },
  });

  const navigate = useNavigate();

  const forgotUsernameRef = useRef();

  const dispatch = useDispatch();

  const [isUsernameNotFound, setIsUsernameNotFound] = useState(false);

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(state, SLICE_LOGIN, THUNK_POST_FORGOT_USERNAME)
  );

  const { isFailure, isSuccess, description } = apiResponses;

  const onBackButtonOnClickHandler = () => {
    navigate(`/${LOGIN_ROUTE}`);
  };

  const handleOnSubmit = async (data) => {
    const token = isReCaptchaEnabled
      ? await forgotUsernameRef.current.onSubmitWithReCAPTCHA()
      : null;
    if (token || !isReCaptchaEnabled) {
      const payload = {
        email: data.email,
        dob: data.dateOfBirth,
        fgtUserIdRecaptchaTkn: token,
      };
      dispatch(
        saveForgotUsernameData({
          email: data.email,
          dateOfBirth: data.dateOfBirth,
        })
      );
      dispatch(postForgotUsername(payload));
    }
  };

  useEffect(() => {
    if (isSuccess && description === USERNAME_SENT_SUCCESSFULLY) {
      navigate(`/${FORGOT_USERNAME_ROUTE}/${FORGOT_USERNAME_EMAIL_SENT}`);
    }
    if (isFailure && description === MATCH_NOT_FOUND) {
      setIsUsernameNotFound(true);
    }
    dispatch(clearLoginApiResponse(THUNK_POST_FORGOT_USERNAME));
  }, [description, isFailure, isSuccess, navigate, dispatch]);

  return (
    <Fragment>
      <BackButton
        id={FORGOT_USERNAME_BACK_BUTTON}
        backButtonOnClickHandler={onBackButtonOnClickHandler}
        backButtonText={intl.formatMessage({ id: BACK_BUTTON_TEXT })}
      />
      <StyledForgotUsernameBox>
        <Stack spacing={{ xs: 2.5, md: 2 }}>
          <StyledTypographyHeader
            id={FORGOT_USERNAME_HEADER}
            component={H1}
            variant={H3}
          >
            {intl.formatMessage({
              id: FORGOT_USERNAME_HEADER,
            })}
          </StyledTypographyHeader>
          <Stack spacing={5}>
            <Typography id={FORGOT_USERNAME_CONTENT_1}>
              {intl.formatMessage({
                id: FORGOT_USERNAME_CONTENT_1,
              })}
            </Typography>
            <FormProvider {...methods}>
              <form
                id={FORGOT_USERNAME_FORM}
                onSubmit={methods.handleSubmit(handleOnSubmit)}
              >
                <ForgotUsernameFormElements />
              </form>
            </FormProvider>
          </Stack>
        </Stack>
      </StyledForgotUsernameBox>
      {isReCaptchaEnabled ? <GoogleReCaptcha ref={forgotUsernameRef} /> : null}
      <NoMatchFoundModal
        flow={FLOW_TEXT_ACCOUNT}
        open={isUsernameNotFound}
        setOpen={setIsUsernameNotFound}
      />
    </Fragment>
  );
};

export default ForgotUserName;
